import type { JSX } from 'react';

import CommaSeparatedList from '~/components/common/CommaSeparatedList';
import { getTicketDocuments } from '~/components/invoices/detailPage/invoiceDocuments';
import { ShowDocumentCell } from '~/components/rowPreview/ShowDocumentCell';
import { colDefTemplate } from '~/components/table/colDefTemplate';
import {
  onlyContainsFilterOperator,
  onlyIsAnyOfFilterOperator,
} from '~/components/table/filterOperators';
import { getApplyQuickFilterFn } from '~/components/table/helpers';
import { yesOrNo } from '~/components/utils/booleanToText';
import { LiftAITextLink } from '~/components/utils/helpers';
import type { Invoice } from '~/data/schemas/invoice';
import type { ServiceProvider } from '~/data/schemas/serviceProvider';
import type { TicketWithPortfolio } from '~/data/schemas/ticket';
import { entryTypeOptions } from '~/data/schemas/ticket';
import { LocaleTerms } from '~/hooks/useLocaleTerms';
import { formatInPropertyTimezone } from '~/utils/timezone';
import { buildUrlForProperty } from '~/utils/urlBuilder';

type GetTicketsTableColumns = {
  /** Add a link to property name that navigates to the property details page */
  linkOnProperty?: boolean;

  /** User can edit tickets */
  canEdit?: boolean;

  /** Callback to edit a ticket */
  getActionsEl: (ticket: TicketWithPortfolio) => JSX.Element[];

  /** Can filter by property */
  canFilterProperty?: boolean;

  /** Locale terms for column headers */
  terms: LocaleTerms;

  /** Puts a "Show document" link under the number of a ticket with an attachment */
  onPreviewDocument?: (ticket: TicketWithPortfolio) => void;
};

const PropertyCell = ({
  value,
  linkOnProperty,
}: {
  value: TicketWithPortfolio['property'];
  linkOnProperty: boolean;
}) => {
  // `value` is null when the ticket's Property was deleted (`Ticket.property` is
  // a SET_NULL FK), so a real ticket can have none — there is nothing to
  // link/name. Render the same `-` placeholder this column's `valueFormatter`
  // uses, rather than an empty link pointing at an undefined property
  // (AMP-585/AMP-593).
  if (!value) {
    return '-';
  }

  return linkOnProperty ? (
    <LiftAITextLink label={value.name} target="_blank" href={buildUrlForProperty(value.id)} />
  ) : (
    value.name
  );
};

export function getTicketsTableColumns({
  linkOnProperty = false,
  canEdit,
  getActionsEl,
  canFilterProperty = true,
  terms,
  onPreviewDocument,
}: GetTicketsTableColumns) {
  const ticketListCols = colDefTemplate<TicketWithPortfolio>({
    options: {
      autoWidth: true,
    },
    cols: [
      {
        field: 'id',
        headerName: 'ID (internal use only)',
        filterable: false,
        // The grid sorts server-side and `id` is not a registered ordering choice,
        // so offering the sort sent `ordering=id` and 400'd the whole page.
        sortable: false,
      },
      {
        field: 'number',
        headerName: 'Ticket #',
        renderCell: ({ row }) => (
          <ShowDocumentCell
            onShowDocument={
              onPreviewDocument && getTicketDocuments(row).length > 0
                ? () => onPreviewDocument(row)
                : undefined
            }
          >
            {row.number}
          </ShowDocumentCell>
        ),
        // The endpoint's `number` filter is a single icontains lookup, so "equals"
        // and "starts with" would have been labels over a contains match.
        filterOperators: onlyContainsFilterOperator,
      },
      {
        field: 'entryType',
        headerName: 'Entry Type',
        type: 'singleSelect',
        minWidth: 200,
        filterOperators: onlyIsAnyOfFilterOperator,
        valueOptions: entryTypeOptions.map(([key, value]) => ({
          value: key,
          label: value,
        })),
      },
      {
        field: 'startedTime',
        headerName: 'Arrival Time',
        valueGetter: (_, row) => new Date(row.startedTime),
        valueFormatter: (_, row) =>
          formatInPropertyTimezone(row.startedTime, row.property?.timeZone),
        renderCell: ({ row }) => formatInPropertyTimezone(row.startedTime, row.property?.timeZone),
        type: 'date',
        filterable: false,
      },
      {
        field: 'property',
        headerName: 'Property',
        nested: { query: 'property.name' },
        filterOperators: onlyContainsFilterOperator,
        filterable: canFilterProperty,
        valueFormatter: (value: TicketWithPortfolio['property']) => value?.name ?? '-',
        renderCell: ({ value }) => <PropertyCell value={value} linkOnProperty={linkOnProperty} />,
        // `value` is null when the ticket's Property was deleted (`SET_NULL`)
        // (AMP-593) — sort it as an empty string rather than throw.
        sortComparator: (prev, curr, ...rest) => (prev?.name ?? '').localeCompare(curr?.name ?? ''),
        getApplyQuickFilterFn(value, colDef, apiRef) {
          return getApplyQuickFilterFn(value, 'property.name');
        },
      },
      {
        field: 'portfolio',
        headerName: 'Portfolio',
        nested: { query: 'portfolio.name' },
        valueFormatter: (value: TicketWithPortfolio['portfolio']) => value?.name,
        renderCell: ({ value }) => value?.name,
        filterable: false,
        // A ticket can have no property (its Property was deleted — SET_NULL FK)
        // or a property with no portfolio, so either side can be null. Sort those
        // as an empty string rather than throwing on `.name` (AMP-585/AMP-593).
        sortComparator: (prev, curr, ...rest) => (prev?.name ?? '').localeCompare(curr?.name ?? ''),
        getApplyQuickFilterFn(value, colDef, apiRef) {
          return getApplyQuickFilterFn(value, 'portfolio.name');
        },
      },
      {
        field: 'carsServiced',
        headerName: terms.vehiclesServiced,
        sortable: false,
        filterable: false,
        valueFormatter: (value: TicketWithPortfolio['carsServiced']) =>
          value.map((car: { name: string }) => car.name).join(', '),
        renderCell: ({ row }) => {
          const items = row.carsServiced.map((item: { name: string }, i: number) => (
            <span key={i}>{item.name}</span>
          ));
          return <CommaSeparatedList items={items} />;
        },
      },
      {
        field: 'invoice',
        headerName: 'Invoice #',
        filterable: false,
        nested: { query: 'invoice.number' },
        valueFormatter: (value: Invoice) => value?.number ?? '-',
        sortComparator: (prev, curr, ...rest) =>
          (prev?.number ?? '').localeCompare(curr?.number ?? ''),
        getApplyQuickFilterFn(value, colDef, apiRef) {
          return getApplyQuickFilterFn(value, 'invoice.number');
        },
      },
      {
        field: 'serviceProviderGroup',
        headerName: 'Provider',
        nested: { query: 'serviceProviderGroup.name' },
        filterable: false,
        filterOperators: onlyContainsFilterOperator,
        valueFormatter: (value: ServiceProvider) => value?.name ?? '-',
        sortComparator: (prev, curr, ...rest) => (prev?.name ?? '').localeCompare(curr?.name ?? ''),
        getApplyQuickFilterFn(value, colDef, apiRef) {
          return getApplyQuickFilterFn(value, 'serviceProvider.name');
        },
      },
      {
        field: 'problemDescription',
        sortable: false,
        filterable: false,
        headerName: 'Problem Description',
      },
      {
        field: 'workPerformed',
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => {
          return row.workPerformed;
        },
        headerName: 'Work Performed',
      },
      {
        field: 'equipmentFailure',
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => yesOrNo(row.equipmentFailure),
        headerName: 'Equipment Related Failure',
      },
      {
        field: 'problemType',
        sortable: false,
        filterable: false,
        valueGetter: (_, row) => {
          return row.problemType;
        },
        headerName: 'Problem Type',
      },
      {
        field: 'callTime',
        headerName: 'Call Time',
        type: 'dateTime',
        filterable: false,
        sortable: false,
        valueGetter: (_, row) => {
          if ('callTime' in row && row.callTime) {
            return new Date(row.callTime);
          }
          return null;
        },
        valueFormatter: (_, row) =>
          'callTime' in row && row.callTime
            ? formatInPropertyTimezone(row.callTime, row.property?.timeZone)
            : '-',
        renderCell: ({ row }) =>
          'callTime' in row && row.callTime
            ? formatInPropertyTimezone(row.callTime, row.property?.timeZone)
            : '-',
      },
      {
        field: 'finishedTime',
        headerName: 'Departure Time',
        type: 'dateTime',
        filterable: false,
        sortable: false,
        valueGetter: (_, row) => new Date(row.finishedTime),
        valueFormatter: (_, row) =>
          formatInPropertyTimezone(row.finishedTime, row.property?.timeZone),
        renderCell: ({ row }) => formatInPropertyTimezone(row.finishedTime, row.property?.timeZone),
      },
      {
        field: 'timeOnSiteRegularHours',
        headerName: 'Time on Site',
        filterable: false,
        sortable: false,
        valueFormatter: (value: string) => value || '-',
      },
      {
        field: 'timeOnSiteOvertime',
        filterable: false,
        sortable: false,
        headerName: 'Time on Site (OT)',
        valueFormatter: (value: string) => value || '-',
      },
      {
        field: 'travelTimeRegularHours',
        headerName: 'Travel Time',
        filterable: false,
        sortable: false,
        valueFormatter: (value: string) => value || '-',
      },
      {
        field: 'travelTimeOvertime',
        headerName: 'Travel Time (OT)',
        filterable: false,
        sortable: false,
        valueFormatter: (value: string) => value || '-',
      },
    ],
  });

  if (canEdit) {
    ticketListCols.push({
      field: 'actions',
      type: 'actions',
      getActions: ({ row: ticket }) => getActionsEl(ticket),
      // TODO Possibly should use `theme.spacing` but it's not available in this context
      width: 70,
      align: 'right',
    });
  }

  return ticketListCols;
}
